/**
 * Shared links (docs/sharing.md): a link anyone can open, without an account, to see and download chosen
 * files from one backup or one folder archive. The rules here are shared by the API, the portal, the share
 * viewer and the assistant.
 */
/** How the files look to the person who opens the link. Adding one: here, a migration widening the CHECK, a viewer component. */
export const SHARE_THEMES = ['gallery', 'list'];
/** End-date choices in the share dialog, in days (null = no end date). 7 is preselected. */
export const SHARE_END_CHOICES = [1, 7, 30, null];
export const SHARE_DEFAULT_END_DAYS = 7;
/** A link may be given an end date at most this far ahead (a typo guard; "no end date" is separate). */
export const SHARE_MAX_END_DAYS = 3650;
/** Links the assistant makes always end, and within this many days (docs/assistant.md A16). */
export const SHARE_ASSISTANT_MAX_DAYS = 30;
export const SHARE_NAME_MAX = 120;
export const SHARE_PASSWORD_MIN = 8;
export const SHARE_PASSWORD_MAX = 128;
/** Pictures the server makes previews of. */
export const SHARE_PREVIEW_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'tif', 'tiff', 'avif', 'bmp'];
/** Pictures too, but shown as file tiles until the server can decode them (phone and camera formats). */
export const SHARE_IMAGE_ONLY_EXTS = ['heic', 'heif', 'cr2', 'cr3', 'nef', 'arw', 'dng', 'raf', 'orf', 'rw2'];
export const SHARE_VIDEO_EXTS = ['mp4', 'mov', 'm4v', 'avi', 'mkv', 'webm', '3gp', 'wmv', 'mts'];
const IMAGE = new Set(SHARE_PREVIEW_EXTS);
const IMAGE_NO_PREVIEW = new Set(SHARE_IMAGE_ONLY_EXTS);
const VIDEO = new Set(SHARE_VIDEO_EXTS);
function extOf(name) {
    const i = name.lastIndexOf('.');
    return i > 0 ? name.slice(i + 1).toLowerCase() : '';
}
export function shareMediaOf(name) {
    const e = extOf(name);
    if (IMAGE.has(e) || IMAGE_NO_PREVIEW.has(e))
        return 'image';
    if (VIDEO.has(e))
        return 'video';
    return 'other';
}
/** Whether the server makes a picture preview of this file (it decodes it in the worker). */
export function sharePreviewable(name) {
    return IMAGE.has(extOf(name));
}
/** The look a share dialog suggests: the gallery when most files are pictures. */
export function suggestShareTheme(names) {
    if (!names.length)
        return 'list';
    const pics = names.filter((n) => shareMediaOf(n) === 'image').length;
    return pics / names.length >= 0.6 ? 'gallery' : 'list';
}
/**
 * The folder every selected path sits in (backup paths: `/C:/Users/dana/Pictures/a.jpg`). Shared items are
 * stored relative to it, so the person opening the link never sees where the files were on the computer.
 */
export function shareCommonParent(paths) {
    if (!paths.length)
        return '/';
    const parentOf = (p) => {
        const i = p.lastIndexOf('/');
        return i <= 0 ? '/' : p.slice(0, i);
    };
    const split = (p) => (p === '/' ? [] : p.slice(1).split('/'));
    // The server passes a single selected folder's own path when the whole folder is shared, so its
    // contents sit at the top of the link; here every path counts from its parent.
    let common = split(parentOf(paths[0]));
    for (const p of paths.slice(1)) {
        const parts = split(parentOf(p));
        let i = 0;
        while (i < common.length && i < parts.length && common[i] === parts[i])
            i++;
        common = common.slice(0, i);
    }
    return common.length ? '/' + common.join('/') : '/';
}
/** `path` relative to `root`, as shown to viewers (`Wedding/img1.jpg`). Empty for the root itself. */
export function shareRelative(root, path) {
    if (root === '/')
        return path.replace(/^\/+/, '');
    if (path === root)
        return '';
    return path.startsWith(root + '/') ? path.slice(root.length + 1) : path.replace(/^\/+/, '');
}
/** A password people can read out or type: 4 groups of 4 letters and digits, no look-alikes (~80 bits). */
export function generateSharePassword(random) {
    const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
    const out = [];
    const bytes = random(64);
    let i = 0;
    while (out.length < 16) {
        const b = bytes[i++ % bytes.length];
        if (b >= alphabet.length * Math.floor(256 / alphabet.length))
            continue; // no modulo bias
        out.push(alphabet[b % alphabet.length]);
        if (i > 2000)
            break;
    }
    return [0, 4, 8, 12].map((k) => out.slice(k, k + 4).join('')).join('-');
}
export function sharePasswordProblem(pw) {
    if (pw.length < SHARE_PASSWORD_MIN)
        return `Use at least ${SHARE_PASSWORD_MIN} characters.`;
    if (pw.length > SHARE_PASSWORD_MAX)
        return `Use at most ${SHARE_PASSWORD_MAX} characters.`;
    return null;
}
//# sourceMappingURL=share.js.map